// Suppress noisy 403s from the Lovable platform analytics endpoint
// (/__l5e/trackevents). We can't stop the platform script from calling it,
// but we can intercept the request before it hits the network so the browser
// never logs a 403 in the console and Sentry never sees it.

const BLOCKED = "/__l5e/trackevents";

function isBlocked(url: string): boolean {
  try {
    return url.includes(BLOCKED);
  } catch {
    return false;
  }
}

export function installTrackeventsSuppressor() {
  if (typeof window === "undefined") return;

  // Patch fetch
  const originalFetch = window.fetch?.bind(window);
  if (originalFetch) {
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.toString()
            : input?.url ?? "";
      if (isBlocked(url)) {
        return Promise.resolve(new Response(null, { status: 204 }));
      }
      return originalFetch(input, init);
    };
  }

  // Patch sendBeacon (used by analytics pings)
  const nav = navigator as Navigator & {
    sendBeacon?: (url: string | URL, data?: BodyInit | null) => boolean;
  };
  const originalBeacon = nav.sendBeacon?.bind(nav);
  if (originalBeacon) {
    nav.sendBeacon = (url: string | URL, data?: BodyInit | null) => {
      if (isBlocked(typeof url === "string" ? url : url.toString())) {
        return true;
      }
      return originalBeacon(url, data);
    };
  }

  // Patch XHR as a safety net
  const OrigXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (
    this: XMLHttpRequest & { __blocked?: boolean },
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    this.__blocked = isBlocked(typeof url === "string" ? url : url.toString());
    return OrigXHROpen.call(this, method, url, ...(rest as [boolean?, string?, string?]));
  } as typeof XMLHttpRequest.prototype.open;

  const OrigXHRSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (
    this: XMLHttpRequest & { __blocked?: boolean },
    body?: Document | XMLHttpRequestBodyInit | null,
  ) {
    if (this.__blocked) return;
    return OrigXHRSend.call(this, body);
  };
}
